import type { Writable } from "ts-essentials";

import mainRefs from "@/__main__/refs.mjs";
import { devWarn } from "@/util/dev.mjs";

// This number is arbitrary and varies by browser, but it should be
// reasonably high.
const STACK_SIZE_WARNING = 5000;

type CloneOptions = {
  objectsOnly: boolean;
};

type InternalState = {
  callStackSize: number;
  originalObj: unknown;
  origin?: string;
};

/**
 * Deep clone utility function, meant for serializable data,
 * since it does not preserve original types.
 *
 * Important: Use caution when using this utility with large
 * objects, as this can cause issues with call-stack depth.
 */
export default function clone<T>(
  obj: T,
  options?: CloneOptions,
): IsAny<T> extends true ? T : Writable<T> {
  // Temporarily disable access warnings while cloning
  mainRefs.enableAccessWarnings = false;

  const cloned = innerClone(obj, options, {
    callStackSize: 0,
    originalObj: obj,
    origin: new Error().stack,
  });

  mainRefs.enableAccessWarnings = true;

  return cloned;
}

function innerClone<T>(
  obj: T,
  options: CloneOptions | undefined,
  internalState: InternalState,
): T {
  if (internalState.callStackSize > STACK_SIZE_WARNING) {
    devWarn(
      "Attempting to clone large object!",
      internalState.originalObj,
      internalState.origin ? `\n${internalState.origin}` : "",
    );
  }

  if (obj instanceof Set) {
    return new Set(obj) as unknown as T;
  }

  if (obj instanceof Uint8Array) {
    return new Uint8Array(obj) as unknown as T;
  }

  if (obj instanceof Uint16Array) {
    return new Uint16Array(obj) as unknown as T;
  }

  const c = (Array.isArray(obj)
    ? [...obj]
    : obj
      ? { ...obj }
      : obj) as unknown as T;

  for (const key in obj) {
    const value = obj[key];
    if (value && typeof value === "object" && !(value instanceof Date)) {
      internalState.callStackSize++;
      c[key] = innerClone(value, options, internalState);
    } else if (options?.objectsOnly) {
      delete c[key];
    }
  }

  return c;
}
